import {MouseEventHandler, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import ICON_BEST_RED from '../../../public/static/comment/icon-best-red.svg';
import ICON_BEST_WHITE from '../../../public/static/comment/icon-best-white.svg';
import ICON_CREATOR_LIGHT from '../../../public/static/comment/icon-creator-light.svg';
import ICON_CREATOR from '../../../public/static/comment/icon-creator.svg';
import Anchor from '../../common/component/Anchor';
import EllipsisText from '../../common/component/EllipsisText';
import SingleText from '../../common/component/SingleText';
import Text from '../../common/component/Text';
import {WEB_HOST} from '../../common/constant';
import {Page} from '../../common/constant/page';
import {openWindow} from '../../common/function';
import useTheme from '../../common/hook/useTheme';
import {ITheme} from '../../common/type';
import TextButton from '../../ui/button/TextButton';
import {IViewType} from '../../viewer/type';
import {CommentThemeContext} from '../common';
import useCommentEvent from '../hook/useCommentEvent';
import {ICommentBadgeType, ICommentContent} from '../type';

interface IProps {
  comment: ICommentContent;
  contentMaxLine?: number;
  viewType?: IViewType;
}

export default function CommentContent({
  comment,
  contentMaxLine,
  viewType,
}: IProps) {
  const {t} = useTranslation();
  const [isShowSpolier, setIsShowSpolier] = useState(false);
  const {
    isOnlyDarkModeBadge,
    createDtTextStyle,
    spoilerTextStyle,
    spoilerInfoTextStyle,
    temporaryTextStyle,
    temporaryUserNameTextStyle,
    userNameTextStyle,
    withdrawUserNameTextStyle,
    contentTextStyle,
    bestBadgeColor,
  } = useContext(CommentThemeContext);

  const {
    id,
    userName,
    isBest,
    isReply,
    isSpolier,
    isWithdraw,
    isBlock,
    isMine,
    createDtText,
    type,
  } = comment;

  const isBlockedComment =
    isBlock || type === 'temporary' || type === 'adminDelete';
  const isCreator = type === 'authorMessage';
  const isShowBadge = !isReply && (isCreator || isBest);
  const hideComment = isSpolier && !isShowSpolier;
  const contentStyle = isBlockedComment
    ? temporaryTextStyle
    : hideComment
      ? spoilerTextStyle
      : contentTextStyle;
  const userNameStyle =
    isWithdraw && withdrawUserNameTextStyle
      ? withdrawUserNameTextStyle
      : isBlockedComment && !isMine
        ? temporaryUserNameTextStyle
        : userNameTextStyle;

  const {sendCommentEvent} = useCommentEvent();
  const onClickSpoiler: MouseEventHandler = e => {
    e.stopPropagation();
    setIsShowSpolier(true);
    sendCommentEvent({
      actionName: 'spoiler',
      action: 'click',
      customProps: {
        comment_id: id,
        event_layer: 'comment',
        viewer_type: viewType,
      },
    });
  };

  const {theme} = useTheme();
  const badgeType: ICommentBadgeType = isCreator
    ? 'creator'
    : bestBadgeColor === 'red'
      ? 'best'
      : 'bestWhite';
  const iconSrc = ICON_MAP[isOnlyDarkModeBadge ? 'dark' : theme][badgeType];

  return (
    <div>
      <div className="flex items-center">
        {isShowBadge && (
          <img
            src={iconSrc}
            alt={isCreator ? 'CREATOR' : 'BEST'}
            className="mr-6"
          />
        )}
        <SingleText
          className={cn('pt-1 flex-auto h-18 leading-18', userNameStyle)}
          width="auto"
        >
          {userName}
        </SingleText>
        <Text
          className={cn(
            'flex-none h-18 leading-18 min-w-[51px] text-right',
            createDtTextStyle
          )}
        >
          {createDtText}
        </Text>
      </div>
      <div className="relative w-full h-full overflow-hidden mt-8">
        {!!contentMaxLine && (
          <EllipsisText
            className={cn('leading-21', contentStyle)}
            line={contentMaxLine}
          >
            <Content {...comment} />
          </EllipsisText>
        )}
        {!contentMaxLine && (
          <Text className={cn('leading-21', contentStyle)}>
            <Content {...comment} />
          </Text>
        )}
        {!isBlockedComment && hideComment && (
          <TextButton
            className="btn-transparent w-full h-full left-0 top-0 !p-0 !absolute"
            textClassName={cn(
              'left-0 top-0 absolute leading-20',
              spoilerInfoTextStyle
            )}
            onClick={onClickSpoiler}
          >
            {t('comment_see_comment_spoiler')}
          </TextButton>
        )}
      </div>
    </div>
  );
}

function Content({type, isBlock, content}: ICommentContent) {
  const {t} = useTranslation();
  const onClickLink = () => {
    openWindow({
      url: `${WEB_HOST}/${Page.MoreRightReport}`,
      windowName: '',
    });
  };

  if (isBlock) {
    return <>{t('comment_block_displayed')}</>;
  }
  return (
    <>
      {(type === 'normal' || type === 'authorMessage') && content}
      {type === 'temporary' && (
        <>
          {t('comment_regulated_content')}
          <br />
          <Anchor pageInfo={{page: Page.MoreRightReport}} onClick={onClickLink}>
            <u>{t('comment_regulated_content_link')}</u>
          </Anchor>
        </>
      )}
      {type === 'adminDelete' && t('comment_policy_deleted_content')}
    </>
  );
}

const ICON_MAP: {
  [key in ITheme]: {[key in ICommentBadgeType]: string};
} = {
  dark: {
    creator: ICON_CREATOR,
    best: ICON_BEST_RED,
    bestWhite: ICON_BEST_WHITE,
  },
  light: {
    creator: ICON_CREATOR_LIGHT,
    best: ICON_BEST_RED,
    bestWhite: ICON_BEST_WHITE,
  },
};
